import { useState } from"react";
import { EXERCICES } from"@/data/exercices";
import { formatEUR, PCG_INDEX } from"@/lib/pcgUtils";
import {
 CheckCircle2,
 XCircle,
 ChevronLeft,
 ChevronRight,
 RotateCcw,
 ArrowRight,
 Trophy,
} from"lucide-react";
import ShareButtons from"@/components/ShareButtons";

const EMPTY_ROW = () => ({ c:"", l:"", d:"", k:"" });

function compareEcritures(user, solution) {
 const norm = (rows) => {
 const map = {};
 for (const r of rows) {
 const num = String(r.c ||"").trim();
 if (!num) continue;
 if (!map[num]) map[num] = { d: 0, k: 0 };
 map[num].d += Number(r.d) || 0;
 map[num].k += Number(r.k) || 0;
 }
 return map;
 };
 const u = norm(user);
 const s = norm(solution);
 const keys = new Set([...Object.keys(u), ...Object.keys(s)]);
 for (const k of keys) {
 const a = u[k] || { d: 0, k: 0 };
 const b = s[k] || { d: 0, k: 0 };
 if (Math.abs(a.d - b.d) > 0.01) return false;
 if (Math.abs(a.k - b.k) > 0.01) return false;
 }
 return true;
}

export default function Exercices({ onExerciceComplete }) {
 const [exIdx, setExIdx] = useState(null);
 const [step, setStep] = useState(0);
 const [rows, setRows] = useState([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
 const [submitted, setSubmitted] = useState(false);
 const [isCorrect, setIsCorrect] = useState(false);
 const [stepScore, setStepScore] = useState([]);

 if (exIdx === null) {
 return (
 <div data-testid="exercices-list" className="space-y-6">
 <div>
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Cas pratiques
 </span>
 <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#15294F] tracking-tight">
 Exercices guidés
 </h2>
 <p className="text-slate-600 mt-3 text-base max-w-2xl">
 Des cas d'entreprise complets sur plusieurs écritures. Idéal pour mettre bout à bout
 tout ce que vous avez appris : du cycle d'achat-vente à la clôture annuelle.
 </p>
 </div>

 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
 {EXERCICES.map((ex, i) => (
 <button
 key={ex.id}
 data-testid={`exercice-card-${ex.id}`}
 onClick={() => {
 setExIdx(i);
 setStep(0);
 setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
 setSubmitted(false);
 setStepScore([]);
 }}
 className="text-left p-6 rounded-xl border border-keyto bg-white shadow-keyto-sm hover:shadow-keyto hover:-translate-y-0.5 transition-all flex flex-col gap-3"
 style={{ borderTop: `3px solid ${ex.couleur}` }}
 >
 <div className="flex items-center justify-between gap-2">
 <span className="px-2 py-0.5 text-[10px] font-semibold rounded border border-keyto bg-slate-50 text-[#15294F] uppercase tracking-wider">
 {ex.difficulte}
 </span>
 <span className="text-xs font-semibold text-slate-500">{ex.duree}</span>
 </div>
 <h3 className="font-heading font-extrabold text-lg text-[#15294F] leading-tight">
 {ex.titre}
 </h3>
 <p className="text-xs text-slate-600 leading-relaxed flex-1">{ex.contexte}</p>
 <div className="flex items-center justify-between mt-2">
 <span className="text-xs text-slate-500 font-semibold">
 {ex.etapes.length} étapes
 </span>
 <span className="btn-primary-gradient text-white text-xs font-semibold py-1.5 px-3 rounded-md flex items-center gap-1">
 Commencer <ArrowRight size={12} />
 </span>
 </div>
 </button>
 ))}
 </div>
 </div>
 );
 }

 const ex = EXERCICES[exIdx];
 const etape = ex.etapes[step];
 const isLast = step === ex.etapes.length - 1;
 const totalD = rows.reduce((s, r) => s + (Number(r.d) || 0), 0);
 const totalK = rows.reduce((s, r) => s + (Number(r.k) || 0), 0);
 const balanced = Math.abs(totalD - totalK) < 0.01 && totalD > 0;

 const updateRow = (i, field, val) => {
 setRows((cur) =>
 cur.map((r, ri) => {
 if (ri !== i) return r;
 const next = { ...r, [field]: val };
 if (field ==="c" && PCG_INDEX[val.trim()] && !r.l) {
 next.l = PCG_INDEX[val.trim()].lib;
 }
 return next;
 })
 );
 };

 const addRow = () => setRows((cur) => [...cur, EMPTY_ROW()]);

 const submit = () => {
 const ok = compareEcritures(rows, etape.solution);
 setIsCorrect(ok);
 setSubmitted(true);
 setStepScore((cur) => {
 const next = [...cur];
 next[step] = { ok };
 // notify completion if this was the last step
 if (isLast && onExerciceComplete) {
 const finalScore = next.filter((s) => s?.ok).length;
 onExerciceComplete(ex.id, finalScore, ex.etapes.length);
 }
 return next;
 });
 };

 const goToStep = (nextStep) => {
 setStep(nextStep);
 setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
 setSubmitted(false);
 setIsCorrect(false);
 };

 const allDone = stepScore.length === ex.etapes.length && stepScore.every((s) => s?.ok);
 const finalScore = stepScore.filter((s) => s?.ok).length;

 return (
 <div data-testid={`exercice-view-${ex.id}`} className="space-y-6">
 <button
 data-testid="exercice-back"
 onClick={() => setExIdx(null)}
 className="text-sm font-semibold text-slate-500 hover:text-[#15294F] flex items-center gap-1"
 >
 <ChevronLeft size={14} /> Retour aux exercices
 </button>

 <div
 className="p-6 rounded-xl border border-keyto bg-white shadow-keyto-sm"
 style={{ borderTop: `3px solid ${ex.couleur}` }}
 >
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] tracking-tight">
 {ex.titre}
 </h2>
 <p className="text-sm text-slate-600 mt-2 leading-relaxed">{ex.contexte}</p>
 <div className="mt-4 flex gap-1">
 {ex.etapes.map((_, i) => (
 <div
 key={i}
 className={`h-1.5 flex-1 rounded-full ${
 i < step
 ? stepScore[i]?.ok
 ?"bg-emerald-500"
 :"bg-rose-400"
 : i === step
 ?"bg-[#2D6BE0]"
 :"bg-slate-200"
 }`}
 />
 ))}
 </div>
 </div>

 {stepScore.length === ex.etapes.length && submitted && isLast ? (
 <div
 data-testid="exercice-completed"
 className="p-8 rounded-xl border border-keyto bg-white shadow-keyto text-center"
 >
 <Trophy size={48} className="mx-auto text-[#2D6BE0] mb-3" />
 <h3 className="font-heading text-2xl font-extrabold text-[#15294F] mb-2">
 Exercice terminé !
 </h3>
 <p className="text-slate-600 mb-5">
 Score : <strong className="text-[#15294F]">{finalScore} / {ex.etapes.length}</strong> étapes réussies
 {allDone &&" — Sans-faute !"}
 </p>

 <div className="mb-6">
 <p className="text-xs text-slate-500 mb-2 font-semibold">Partagez votre réussite</p>
 <div className="flex justify-center">
 <ShareButtons
 kind="exercice"
 scoreText={`Score : ${finalScore}/${ex.etapes.length} sur l'exercice « ${ex.titre} ».`}
 />
 </div>
 </div>

 <div className="flex gap-3 justify-center flex-wrap">
 <button
 data-testid="exercice-restart"
 onClick={() => {
 setStep(0);
 setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
 setSubmitted(false);
 setStepScore([]);
 }}
 className="bg-white text-[#15294F] font-semibold py-2.5 px-5 rounded-md border border-keyto shadow-keyto-sm hover:bg-slate-50 text-sm flex items-center gap-2"
 >
 <RotateCcw size={14} /> Refaire
 </button>
 <button
 onClick={() => setExIdx(null)}
 className="btn-primary-gradient text-white font-semibold py-2.5 px-5 rounded-md shadow-keyto-sm text-sm"
 >
 Choisir un autre exercice
 </button>
 </div>
 </div>
 ) : (
 <div className="bg-white border border-keyto rounded-xl shadow-keyto p-6 md:p-8">
 <div className="flex items-center justify-between mb-3">
 <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0]">
 Étape {etape.n} / {ex.etapes.length}
 </span>
 </div>

 <p className="text-base md:text-lg text-[#15294F] leading-relaxed font-medium mb-6">
 {etape.enonce}
 </p>

 <div className="bg-slate-50 border border-keyto rounded-lg overflow-hidden mb-4">
 <div className="grid grid-cols-12 bg-[#15294F] text-white text-xs font-semibold p-3 uppercase tracking-wider">
 <div className="col-span-2">Compte</div>
 <div className="col-span-5">Libellé</div>
 <div className="col-span-2 text-right">Débit</div>
 <div className="col-span-2 text-right">Crédit</div>
 <div className="col-span-1"></div>
 </div>
 {rows.map((r, i) => (
 <div
 key={i}
 className="grid grid-cols-12 p-2 border-b border-slate-200 last:border-b-0 items-center gap-2 bg-white"
 >
 <input
 data-testid={`ex-row-${i}-compte`}
 value={r.c}
 onChange={(e) => updateRow(i,"c", e.target.value)}
 disabled={submitted}
 className="col-span-2 font-mono font-bold px-2 py-1.5 border border-keyto rounded-md bg-white text-sm focus:outline-none focus:border-[#2D6BE0] focus:ring-1 focus:ring-[#2D6BE0]"
 placeholder="ex: 401"
 />
 <input
 value={r.l}
 onChange={(e) => updateRow(i,"l", e.target.value)}
 disabled={submitted}
 className="col-span-5 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm focus:outline-none focus:border-[#2D6BE0] focus:ring-1 focus:ring-[#2D6BE0]"
 placeholder="Libellé"
 />
 <input
 data-testid={`ex-row-${i}-debit`}
 type="number"
 step="0.01"
 value={r.d}
 onChange={(e) => updateRow(i,"d", e.target.value)}
 disabled={submitted}
 className="col-span-2 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm text-right font-mono focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
 placeholder="0"
 />
 <input
 data-testid={`ex-row-${i}-credit`}
 type="number"
 step="0.01"
 value={r.k}
 onChange={(e) => updateRow(i,"k", e.target.value)}
 disabled={submitted}
 className="col-span-2 px-2 py-1.5 border border-keyto rounded-md bg-white text-sm text-right font-mono focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500"
 placeholder="0"
 />
 <button
 onClick={() => setRows((cur) => cur.filter((_, ri) => ri !== i))}
 disabled={submitted || rows.length <= 2}
 className="col-span-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 text-xs font-bold"
 >
 ×
 </button>
 </div>
 ))}
 <div className="grid grid-cols-12 p-3 bg-slate-100 border-t border-slate-200 font-mono text-sm font-bold">
 <div className="col-span-7 uppercase tracking-wider text-xs text-slate-600">
 {balanced ?"✓ Équilibré" :"Total"}
 </div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalD)}</div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalK)}</div>
 <div className="col-span-1"></div>
 </div>
 </div>

 <div className="flex flex-wrap gap-3">
 {!submitted ? (
 <>
 <button
 onClick={addRow}
 className="bg-white text-[#15294F] font-semibold py-2.5 px-4 rounded-md border border-keyto shadow-keyto-sm hover:bg-slate-50 text-sm"
 >
 + Ajouter une ligne
 </button>
 <button
 data-testid="ex-submit"
 onClick={submit}
 disabled={!balanced}
 className="btn-primary-gradient text-white font-semibold py-2.5 px-6 rounded-md shadow-keyto-sm disabled:opacity-50 text-sm"
 >
 Valider l'étape
 </button>
 </>
 ) : (
 <>
 {!isLast && (
 <button
 data-testid="ex-next-step"
 onClick={() => goToStep(step + 1)}
 className="btn-primary-gradient text-white font-semibold py-2.5 px-6 rounded-md shadow-keyto-sm text-sm flex items-center gap-2"
 >
 Étape suivante <ChevronRight size={14} />
 </button>
 )}
 <button
 data-testid="ex-retry"
 onClick={() => {
 setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
 setSubmitted(false);
 setStepScore((cur) => {
 const next = [...cur];
 next[step] = undefined;
 return next;
 });
 }}
 className="bg-white text-[#15294F] font-semibold py-2.5 px-4 rounded-md border border-keyto shadow-keyto-sm hover:bg-slate-50 text-sm flex items-center gap-2"
 >
 <RotateCcw size={14} /> Réessayer
 </button>
 </>
 )}
 </div>

 {submitted && (
 <div
 data-testid="ex-result"
 className={`mt-6 p-5 rounded-lg border ${
 isCorrect ?"bg-emerald-50 border-emerald-200" :"bg-rose-50 border-rose-200"
 }`}
 >
 <div className="flex items-center gap-3 mb-3">
 {isCorrect ? (
 <CheckCircle2 className="text-emerald-700" size={24} />
 ) : (
 <XCircle className="text-rose-700" size={24} />
 )}
 <h3 className="font-heading text-xl font-extrabold text-[#15294F]">
 {isCorrect ?"Étape validée !" :"Pas tout à fait..."}
 </h3>
 </div>
 <p className="text-sm text-slate-700 leading-relaxed mb-4">{etape.explication}</p>
 <div className="bg-white border border-keyto rounded-lg overflow-hidden">
 <div className="grid grid-cols-12 bg-[#15294F] text-white text-xs font-semibold p-2 uppercase tracking-wider">
 <div className="col-span-2">Compte</div>
 <div className="col-span-6">Libellé attendu</div>
 <div className="col-span-2 text-right">Débit</div>
 <div className="col-span-2 text-right">Crédit</div>
 </div>
 {etape.solution.map((l, i) => (
 <div
 key={i}
 className="grid grid-cols-12 p-2 border-b border-slate-100 last:border-b-0 font-mono text-sm items-center"
 >
 <div className="col-span-2 font-bold text-[#15294F]">{l.c}</div>
 <div className="col-span-6 font-sans text-slate-700">{l.l}</div>
 <div className="col-span-2 text-right text-emerald-700 font-bold">
 {formatEUR(l.d)}
 </div>
 <div className="col-span-2 text-right text-rose-700 font-bold">
 {formatEUR(l.k)}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
}
